import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAutoTopupLock,
  putAutoTopupLock,
} from '../../apis/api/autoTopupDebug';

interface AutoTopupDebugModalProps {
  isOpen: boolean;
  organizationId: string;
  orgName: string;
  onClose: () => void;
}

interface LockStatus {
  locked: boolean;
  ttl_seconds: number;
}

/**
 * 自动充值并发锁「调试面板」。
 * 给测试/技术同学在 admin 后台直接预置 / 查看某组织的自动充值并发锁，
 * 无需接触 redis-cli / SSH 隧道 / IAM token，即可复现「已有充值进行中」场景做前端验证。
 * 入口由功能码 auto_topup_debug_manage 控制（仅分配给技术角色，运营不可见）。
 */
export default function AutoTopupDebugModal({
  isOpen,
  organizationId,
  orgName,
  onClose,
}: AutoTopupDebugModalProps) {
  const [status, setStatus] = useState<LockStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<'set' | null>(null);
  const [error, setError] = useState('');
  // 请求序号：用于忽略过期请求的响应（竞态防护）。组织切换后旧组织的 in-flight 请求
  // 可能晚于新状态重置返回并覆盖新组织状态，每次 refresh 递增序号，响应回来时若序号
  // 已不是当前值（说明期间又发起了新请求或切换了组织）则丢弃该响应。
  const requestSeqRef = useRef(0);

  // 打开时自动刷新一次当前锁状态
  const refresh = useCallback(async () => {
    if (!organizationId) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await getAutoTopupLock({ organization_id: organizationId });
      if (seq !== requestSeqRef.current) return; // 过期响应：期间已切换组织/关闭，丢弃
      setStatus(res?.data ?? null);
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setError(`Failed to query lock status: ${String(e)}`);
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [organizationId]);

  // 打开 / 切换目标组织时：先作废旧组织的 in-flight 请求并清空状态，再发起新查询。
  // 组件始终挂载（isOpen=false 仅 return null），state 不会自动重置，若不清理，重新打开
  // 另一个组织时 refresh() 返回前会短暂展示上一个组织的锁状态（误导调试判断）。
  // 注意：**必须合并在同一个 effect 里**——若拆成两个相同依赖的 effect，React 按声明顺序
  // 执行时，先跑的 refresh() 会先 ++requestSeq 发起请求，后跑的 reset 再 ++requestSeq 把
  // 刚发起的请求作废，导致初始查询被丢弃、UI 误显示 Not Locked。
  useEffect(() => {
    if (!isOpen || !organizationId) {
      // 关闭弹窗：作废旧请求并清空状态
      requestSeqRef.current += 1;
      setStatus(null);
      setError('');
      setBusyAction(null);
      setLoading(false);
      return;
    }
    // 打开/切换：作废上一个组织的请求，从干净状态开始，再发起当前组织的新查询
    requestSeqRef.current += 1;
    setStatus(null);
    setError('');
    setBusyAction(null);
    refresh();
  }, [isOpen, organizationId, refresh]);

  const handleSetLock = async () => {
    const seq = ++requestSeqRef.current;
    setBusyAction('set');
    setError('');
    try {
      const res = await putAutoTopupLock(
        { organization_id: organizationId },
        { ttl_seconds: 300 }
      );
      if (seq !== requestSeqRef.current) return; // 期间已切换组织/关闭，丢弃过期响应
      setStatus(res?.data ?? null);
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setError(`Failed to preset lock: ${String(e)}`);
    } finally {
      if (seq === requestSeqRef.current) {
        setBusyAction(null);
      }
    }
  };

  if (!isOpen) return null;

  const isLocked = status?.locked ?? false;
  const ttlText = isLocked ? `${Math.max(0, status?.ttl_seconds ?? 0)}s` : '-';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-[10px] w-full max-w-[520px] shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-indigo-200 bg-indigo-50">
          <div className="shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Auto Top-Up Concurrency Lock Debug
            </h3>
            <p className="text-xs text-gray-600 mt-0.5 break-all">
              {orgName} · {organizationId}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <div className="bg-gray-50 border border-gray-200 rounded-md px-4 py-3.5 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-gray-600">Current Lock Status</span>
              {loading ? (
                <span className="text-xs text-gray-400">Refreshing…</span>
              ) : isLocked ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  Locked · TTL {ttlText}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  Not Locked
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              Preset a lock to simulate &quot;an auto top-up is in progress&quot;,
              then verify the confirm modal on the user console. This presets the
              auto top-up lock; force top-up uses its own independent lock, so a
              preset here does NOT block a force top-up. TTL (300s) is only a crash
              fallback; normal flow releases the lock via defer.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-3.5 py-2.5 mb-4 text-xs text-red-600 break-all">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              className="inline-flex items-center gap-1.5 px-4 py-2 h-10 bg-indigo-600 border-none rounded-md text-sm font-medium text-white cursor-pointer transition-colors duration-200 hover:enabled:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSetLock}
              disabled={loading || busyAction !== null}
            >
              {busyAction === 'set' && (
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {isLocked ? 'Re-preset Lock' : 'Preset Lock'}
            </button>
            <button
              className="inline-flex items-center gap-1.5 px-4 py-2 h-10 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 cursor-pointer transition-all duration-200 hover:enabled:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={refresh}
              disabled={loading || busyAction !== null}
            >
              Refresh Status
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            className="px-5 py-2.5 h-11 bg-white border border-gray-300 rounded-md text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 hover:enabled:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
